import { DestroyRef, inject, Injectable, signal } from '@angular/core';

// Owns the browser's online/offline events, so callers await connectivity instead of polling `navigator.onLine` themselves.
@Injectable()
export class NetworkMonitorService {
  private readonly onlineValue = signal(navigator.onLine);
  private readonly offlineEvents = signal(0);
  private readonly returned = new Set<() => void>();
  private readonly reconnects = new Set<() => void>();

  public readonly online = this.onlineValue.asReadonly();
  public readonly offlineEventsSinceStart = this.offlineEvents.asReadonly();

  public constructor() {
    const goOnline = (): void => {
      this.onlineValue.set(true);

      // Copied before iterating: a `whenOnline` listener removes itself as it runs.
      for (const listener of [...this.returned, ...this.reconnects]) {
        listener();
      }
    };
    const goOffline = (): void => {
      this.onlineValue.set(false);
      this.offlineEvents.update(count => count + 1);
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      this.returned.clear();
      this.reconnects.clear();
    });
  }

  /** Runs on every return from offline, and never on the initial online state. */
  public onReconnect(listener: () => void): () => void {
    this.reconnects.add(listener);

    return (): void => { this.reconnects.delete(listener); };
  }

  /** Runs once the display is back online, immediately if it never left. */
  public whenOnline(listener: () => void): () => void {
    if (this.onlineValue()) {
      listener();

      return (): void => undefined;
    }

    const once = (): void => {
      this.returned.delete(once);
      listener();
    };
    this.returned.add(once);

    return (): void => { this.returned.delete(once); };
  }
}
